import { CONTRACT, RONIN_GAS_LIMIT, useContract } from '@apeiron/contract'
import { CustomError } from '@apeiron/temp'
import { ERROR_CODE } from '@src/constants/error'
import { RETRY } from '@src/constants/retry'
import { deserializeEvent } from '@src/contracts/deserialize/morphPlanet'
import { t } from 'i18next'
import pRetry from 'p-retry'
import { useCallback, useState } from 'react'

const {
  MORPH: { REQUEST_BORN, CHECK_BORN }
} = CONTRACT

const useMorph = (): Result => {
  const requestMorph = useContract(REQUEST_BORN)

  const checkMorph = useContract(CHECK_BORN)

  const [data, setData] = useState<Response | null>(null)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const { coreId } = request

      const retryOperation = async (requestId: string): Promise<Response> => {
        const result = await checkMorph.call(requestId)

        const isDone = result[2]

        if (isDone) {
          const planetId = result[1]

          return planetId
        }

        throw new Error('request is still not done')
      }

      const morphResult = await requestMorph.sendWithOption(
        {
          ronin: {
            gasLimit: RONIN_GAS_LIMIT.MORPH,
            value: RONIN_GAS_LIMIT.VRF
          },
          isVrf: true
        },
        coreId
      )

      const requestId = deserializeEvent(morphResult) as string

      if (!requestId) {
        throw new CustomError(
          t('morph.error.no_born_result'),
          ERROR_CODE.MORPH.CANNOT_GET_MORPH_RESPONSE
        )
      }

      // retry and pull
      const planetId = await pRetry(
        () => retryOperation(requestId),
        RETRY.CONTRACT.BORN
      )

      setData(planetId)

      return planetId
    },
    [requestMorph, checkMorph]
  )

  return {
    fetch,
    data
  }
}

type Request = {
  coreId: string
}

type Response = string

type Result = {
  fetch: (request: Request) => Promise<Response>
  data: Response | null
}

export default useMorph
